import { useEffect, useId, useRef, useState } from "react";
import type { SelectedConsultSlot } from "./consult_availability";
import OfficeToHomeWarning from "./office_to_home_warning";
import type { Option } from "./shared";

interface Props {
  slots: SelectedConsultSlot[];
  visibleDates: string[];
  locationOptions: Option[];
  visitTypeOptions: Option[];
  onRemove: (slot: SelectedConsultSlot) => void;
  onLocationChange: (slotKey: string, value: string) => void;
  onVisitTypeToggle: (slotKey: string, visitType: string, checked: boolean) => void;
}

function groupByOffer(slots: SelectedConsultSlot[]) {
  const groups = new Map<string, SelectedConsultSlot[]>();
  slots.forEach((slot) => {
    const key = JSON.stringify([slot.devaLocation, [...slot.offeredVisitTypes].sort()]);
    const group = groups.get(key) || [];
    group.push(slot);
    groups.set(key, group);
  });
  return Array.from(groups);
}

export default function SelectedConsultSlots({ slots, visibleDates, locationOptions, visitTypeOptions, onRemove, onLocationChange, onVisitTypeToggle }: Props) {
  const id = useId();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const showDetails = expanded || slots.some((slot) => slot.offeredVisitTypes.length === 0);
  const locationRef = useRef<HTMLSelectElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const editingSlot = slots.find((slot) => slot.key === editingKey);
  const editingSlotKey = editingSlot?.key;
  const outsideDayCount = slots.filter((slot) => !visibleDates.includes(slot.date)).length;
  const days = new Map<string, SelectedConsultSlot[]>();
  slots.forEach((slot) => {
    const day = days.get(slot.date) || [];
    day.push(slot);
    days.set(slot.date, day);
  });

  useEffect(() => {
    if (editingSlotKey) locationRef.current?.focus();
    else setEditingKey(null);
  }, [editingSlotKey]);

  const closeEditor = () => {
    if (editingKey) document.getElementById(`${id}-slot-${editingKey}`)?.focus();
    setEditingKey(null);
  };

  const removeSlot = (slot: SelectedConsultSlot) => {
    const index = slots.findIndex((item) => item.key === slot.key);
    const nextSlot = slots[index + 1] || slots[index - 1];
    if (nextSlot) document.getElementById(`${id}-slot-${nextSlot.key}`)?.focus();
    else headingRef.current?.focus();
    if (editingKey === slot.key) setEditingKey(null);
    onRemove(slot);
  };

  return (
    <section className="mt-4 border-t border-neutral-200 pt-3 dark:border-neutral-800" aria-labelledby={`${id}-heading`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 ref={headingRef} id={`${id}-heading`} tabIndex={-1} className="sr-only">Selected slots</h2>
        <span aria-live="polite" className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
          {slots.length} {slots.length === 1 ? "slot" : "slots"} selected
          {outsideDayCount > 0 ? ` · ${outsideDayCount} outside these days` : ""}
        </span>
        <button type="button" className="rounded py-1 text-sm font-medium text-emerald-700 pointer-coarse:min-h-11 dark:text-emerald-300"
          aria-expanded={showDetails} aria-controls={`${id}-details`} onClick={() => setExpanded(!showDetails)}>
          {showDetails ? "Hide selected" : "Review selected"} <span aria-hidden="true">{showDetails ? "⌃" : "⌄"}</span>
        </button>
      </div>
      {!showDetails ? <OfficeToHomeWarning slots={slots.filter((slot) => !visibleDates.includes(slot.date))} /> : null}
      <div id={`${id}-details`} hidden={!showDetails}>
        <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">Select a time to edit its location or visit types.</p>

        {slots.length > 0 ? (
          <div className="mt-3 space-y-3">
            {Array.from(days, ([date, daySlots]) => (
              <section key={date} aria-labelledby={`${id}-day-${date}`}>
                <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <h3 id={`${id}-day-${date}`} className="text-sm font-semibold text-neutral-950 dark:text-white">{daySlots[0].dateLabel}</h3>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {daySlots.length} {daySlots.length === 1 ? "time" : "times"}
                    {!visibleDates.includes(date) ? " · Outside visible days" : ""}
                  </span>
                </header>

                <OfficeToHomeWarning slots={daySlots} />

                <div className="mt-1.5 space-y-2">
                  {groupByOffer(daySlots).map(([groupKey, group], index) => {
                    const slot = group[0];
                    const offeredLabels = visitTypeOptions.filter((option) => slot.offeredVisitTypes.includes(option.value)).map((option) => option.label);
                    const locationLabel = slot.devaLocation === "office" ? "Office" : slot.devaLocation === "home" ? "Home" : slot.devaLocationLabel;
                    const labelId = `${id}-offer-${date}-${index}`;

                    return (
                      <div key={groupKey} role="group" aria-labelledby={labelId}>
                        <p id={labelId} className="mb-1 text-xs text-neutral-600 dark:text-neutral-300">
                          Deva: {locationLabel} · {offeredLabels.length > 0 ? offeredLabels.join(" · ") : <span className="font-medium text-red-600 dark:text-red-300">Choose at least one visit type.</span>}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {group.map((slot) => (
                            <div key={slot.key} className={`inline-flex rounded-md border ${editingKey === slot.key ? "border-emerald-500 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950" : "border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900"}`}>
                              <button
                                id={`${id}-slot-${slot.key}`}
                                type="button"
                                aria-label={`Edit ${slot.scheduleLabel}`}
                                aria-expanded={editingKey === slot.key}
                                aria-controls={editingKey === slot.key ? `${id}-editor` : undefined}
                                className="flex min-h-8 items-center gap-1.5 rounded-l-md px-2 py-1 text-sm font-medium text-neutral-900 hover:bg-neutral-100 pointer-coarse:min-h-11 dark:text-neutral-100 dark:hover:bg-neutral-800"
                                onClick={() => editingKey === slot.key ? closeEditor() : setEditingKey(slot.key)}
                              >
                                <span className="whitespace-nowrap">{slot.timeLabel}</span>
                                {slot.custom ? <span className="text-xs font-normal text-emerald-700 dark:text-emerald-300">Custom</span> : null}
                              </button>
                              <button
                                type="button"
                                aria-label={`Remove ${slot.scheduleLabel}`}
                                className="flex min-h-8 min-w-8 items-center justify-center rounded-r-md border-l border-neutral-200 text-neutral-500 hover:bg-red-50 hover:text-red-700 pointer-coarse:min-h-11 pointer-coarse:min-w-11 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                                onClick={() => removeSlot(slot)}
                              >
                                <span aria-hidden="true">×</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {editingSlot?.date === date ? (
                  <div id={`${id}-editor`} role="group" aria-label={`Edit ${editingSlot.scheduleLabel}`} className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-900"
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        closeEditor();
                      }
                    }}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-neutral-950 dark:text-white">{editingSlot.timeLabel}{editingSlot.custom ? " · Custom" : ""}</p>
                      <button type="button" className="app-button-secondary !py-1 pointer-coarse:min-h-11" onClick={closeEditor}>Done</button>
                    </div>
                    <div className="grid items-start gap-3 sm:grid-cols-[13rem_minmax(0,1fr)]">
                      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                        Deva location
                        <select ref={locationRef} aria-label={`Deva location for ${editingSlot.scheduleLabel}`} className="app-select !mt-1 !py-1.5 pointer-coarse:text-base" value={editingSlot.devaLocation}
                          onChange={(event) => onLocationChange(editingSlot.key, event.target.value)}>
                          {locationOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </label>
                      <fieldset className="min-w-0">
                        <legend className="text-xs font-medium text-neutral-600 dark:text-neutral-300">Visit types offered</legend>
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-2">
                          {visitTypeOptions.map((option) => {
                            const disabled = editingSlot.devaLocation === "home" && option.value === "in_person";
                            return (
                              <label key={option.value} className={`flex items-center gap-1.5 text-sm pointer-coarse:min-h-11 ${disabled ? "opacity-40" : "text-neutral-700 dark:text-neutral-200"}`}>
                                <input type="checkbox" disabled={disabled} checked={editingSlot.offeredVisitTypes.includes(option.value)}
                                  onChange={(event) => onVisitTypeToggle(editingSlot.key, option.value, event.target.checked)}
                                  className="h-4 w-4 rounded border-neutral-300 accent-emerald-600 dark:border-neutral-700 dark:bg-neutral-900" />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </fieldset>
                    </div>
                  </div>
                ) : null}
              </section>
            ))}
          </div>
        ) : <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">Choose at least one slot above.</p>}
      </div>
    </section>
  );
}
